import type { Roles } from '@/auth/roles'

/**
 * Which affordances to render for editing an initiative's content.
 *
 * Mirrors `api/src/shared/domain/shapeAuthority.ts`. The server is the
 * authority — it returns `403 SHAPE_AFTER_APPROVAL` regardless of what the
 * SPA renders — but hiding a control the caller cannot use is better than
 * offering a button that always fails.
 *
 * The rule: any reader may shape while the initiative is still pre-Gate-1;
 * from Experiment onwards it takes InitiativeContributor or Administrator.
 * Every TIS user holds `Reader`, so this is what lets an idea be refined
 * by the people who had it, without also handing the whole tenant write
 * access to approved commitments.
 *
 * Keep this list in step with the API's `SHAPING_STAGES`. Two copies is
 * the cost of the SPA not having the funnel model; the API is the one that
 * enforces, so drift degrades the UI rather than the boundary.
 */
export const SHAPING_STAGES = ['Idea', 'Shaped'] as const

export function isShapingStage(stageCode: string): boolean {
  return (SHAPING_STAGES as readonly string[]).includes(stageCode)
}

export function canShape(roles: Roles, stageCode: string): boolean {
  if (roles.isContributor) return true
  return roles.isReader && isShapingStage(stageCode)
}
